import type { FlightCardData } from '@massilia/queries'

import { cn } from '@massilia/ui'
import { Backpack, ChevronDown, ChevronUp, Luggage } from 'lucide-react'

import { InstallmentMention } from './installment-mention'

interface FlightCardChipsProps {
  flight: FlightCardData
  hasAlternativeOptions?: boolean
  isExpanded?: boolean
  onToggleExpand?: () => void
  totalOptionsCount?: number
}

/**
 * Pied de la carte : bagages, financement et accès aux horaires alternatifs.
 *
 * L'ancienne version empilait trois blocs faisant chacun plus de la moitié de la
 * largeur, ce qui produisait quatre lignes sur mobile. Ici tout tient sur une
 * rangée, avec des libellés courts sur mobile et complets sur desktop, où la
 * place ne manque pas.
 */
export function FlightCardChips({
  flight,
  hasAlternativeOptions = false,
  isExpanded = false,
  onToggleExpand,
  totalOptionsCount = 0,
}: FlightCardChipsProps) {
  const cabinBags = flight.minCabinBags
  const checkedBags = flight.minCheckedBags
  const checkedBagsWeightKg = flight.minCheckedBagsWeightKg ?? 23
  const totalCheckedWeight = checkedBags * checkedBagsWeightKg
  let checkedBagBgClass = 'bg-green-400'
  if (totalCheckedWeight >= 46) {
    checkedBagBgClass = 'bg-green-600'
  } else if (totalCheckedWeight >= 30) {
    checkedBagBgClass = 'bg-green-500'
  }

  // Mobile : une seule rangée, qui défile latéralement si besoin.
  //
  // Desktop large (`xl`) : trois pistes `1fr auto 1fr`. Les deux pistes latérales
  // étant égales, le bloc financement tombe au centre exact — et il y reste que
  // le bouton « autres horaires » soit rendu ou non, puisque sa piste existe
  // toujours. `justify-between` le renvoyait à droite dès que l'offre n'avait pas
  // d'alternative horaire, donc d'une carte à l'autre dans la même liste.
  //
  // Entre `lg` et `xl`, la colonne de gauche ne mesure que 324px une fois le
  // panneau de filtres déduit : rien n'y tient à trois de front, la grille y
  // débordait de 55px. On y garde donc le repli en lignes.
  //
  // Les deux bagages sont en colonne dès `lg` : côte à côte, les quatre pastilles
  // font 735px pour 636 disponibles à 1440.
  return (
    <div className="flex items-center gap-1.5 overflow-x-auto py-0.5 lg:flex-wrap lg:justify-between lg:gap-x-3 lg:gap-y-1.5 lg:overflow-x-visible xl:grid xl:grid-cols-[1fr_auto_1fr] xl:items-center xl:gap-y-0">
      {/* `contents` sur mobile : les deux pastilles restent des enfants directs
          de la rangée défilante, et ne forment une colonne qu'à partir de `lg`. */}
      <div className="contents lg:flex lg:flex-col lg:items-start lg:gap-1.5">
        {checkedBags > 0 ? (
          <span
            className={cn(
              'flex shrink-0 items-center gap-1 rounded px-1.5 py-1 text-[11px] font-bold whitespace-nowrap text-white lg:text-xs',
              checkedBagBgClass
            )}
          >
            <Luggage aria-hidden className="size-3.5 lg:size-4" />
            {checkedBags > 1 ? (
              <>
                <span className="lg:hidden">
                  Soute {checkedBags} × {checkedBagsWeightKg} kg
                </span>
                {/* Le total en kilos reste sur desktop : c'est l'information que
                  l'on compare d'une offre à l'autre quand la place le permet. */}
                <span className="hidden lg:inline">
                  Soute · {checkedBags} × {checkedBagsWeightKg} kg ({totalCheckedWeight} kg)
                </span>
              </>
            ) : (
              <>
                <span className="lg:hidden">Soute {totalCheckedWeight} kg</span>
                <span className="hidden lg:inline">Soute · {totalCheckedWeight} kg</span>
              </>
            )}
          </span>
        ) : (
          <span className="flex shrink-0 items-center gap-1 rounded bg-gray-100 px-1.5 py-1 text-[11px] font-medium whitespace-nowrap text-gray-600 lg:text-xs">
            <Luggage aria-hidden className="size-3.5 text-gray-400 lg:size-4" />
            <span className="lg:hidden">Sans soute</span>
            <span className="hidden lg:inline">Pas de bagage en soute</span>
          </span>
        )}

        {cabinBags > 0 ? (
          <span className="flex shrink-0 items-center gap-1 rounded bg-gray-100 px-1.5 py-1 text-[11px] font-medium whitespace-nowrap text-gray-700 lg:text-xs">
            <Backpack aria-hidden className="size-3.5 text-gray-500 lg:size-4" />
            {/* « Bagage » est masculin : inclus, pas incluse. */}
            <span className="lg:hidden">Cabine inclus</span>
            <span className="hidden lg:inline">Bagage cabine inclus</span>
          </span>
        ) : (
          <span className="flex shrink-0 items-center gap-1 rounded bg-gray-100 px-1.5 py-1 text-[11px] font-medium whitespace-nowrap text-gray-600 lg:text-xs">
            <Backpack aria-hidden className="size-3.5 text-gray-400 lg:size-4" />
            <span className="lg:hidden">Sans cabine</span>
            <span className="hidden lg:inline">Pas de bagage cabine</span>
          </span>
        )}
      </div>

      {/* Le financement se lit dans la section vol sur desktop, où la colonne de
          prix serait sinon trop chargée ; sur mobile il accompagne le prix. */}
      <div className="hidden shrink-0 lg:block">
        <InstallmentMention flight={flight} variant="full" />
      </div>

      {/*
        Piste de droite. Elle reste présente sur desktop même sans alternative
        horaire, sinon le bloc financement ne serait plus au même endroit d'une
        carte à l'autre. Le libellé « Pas d'alternative horaire » ne revient pas
        pour autant : une absence n'a pas besoin d'être écrite.
      */}
      <div className="contents lg:flex lg:justify-end">
        {hasAlternativeOptions && onToggleExpand && (
          <button
            aria-expanded={isExpanded}
            className="ml-auto flex shrink-0 cursor-pointer items-center gap-1 rounded-full border border-sky-200 bg-sky-50 px-2 py-1 text-[11px] font-medium text-sky-700 transition-colors hover:bg-sky-100 lg:ml-0 lg:px-3 lg:text-xs"
            onClick={onToggleExpand}
            type="button"
          >
            <span className="lg:hidden">Autres horaires</span>
            <span className="hidden lg:inline">
              <b>{totalOptionsCount}</b> autres horaires disponibles
            </span>
            {isExpanded ? <ChevronUp className="size-3" /> : <ChevronDown className="size-3" />}
          </button>
        )}
      </div>
    </div>
  )
}
